/**
 * Date formatting.
 *
 * Dates follow the same rule as numbers: the presentation is pinned rather
 * than inherited from the browser, so `03/04/2026` never means March in one
 * session and April in the next. An explicit month name removes the ambiguity
 * entirely.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.5
 */
import { EN_DASH, NUMERIC_LOCALE } from './constants';

/** A value that may or may not be a usable date. */
export type DateInput = Date | string | number | null | undefined;

/** Parses loosely, returning null rather than an Invalid Date. */
export function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined) return null;

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `4 Mar 2026` — unambiguous in every locale. */
export function formatDate(value: DateInput): string {
  const date = toDate(value);
  if (date === null) return '';

  return new Intl.DateTimeFormat(NUMERIC_LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

/** `4 Mar 2026, 14:30` — 24-hour, so no AM/PM ambiguity. */
export function formatDateTime(value: DateInput): string {
  const date = toDate(value);
  if (date === null) return '';

  return new Intl.DateTimeFormat(NUMERIC_LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

/** A date range joined by an en dash, matching {@link formatRange}. */
export function formatDateRange(from: DateInput, to: DateInput): string {
  const start = formatDate(from);
  const end = formatDate(to);

  if (start === '' && end === '') return '';
  if (start === '') return end;
  if (end === '') return start;

  return `${start}${EN_DASH}${end}`;
}

/**
 * Whole days between two instants, truncated toward zero.
 *
 * Used for "days in stage" and stall detection, so it must not round up: a
 * record that entered a stage 13.6 days ago has been there 13 days, and
 * reporting 14 against a 14-day threshold would flag it a day early.
 */
export function daysBetween(from: DateInput, to: DateInput = new Date()): number | null {
  const start = toDate(from);
  const end = toDate(to);
  if (start === null || end === null) return null;

  return Math.trunc((end.getTime() - start.getTime()) / 86_400_000);
}
